import { useState } from 'react';
import { Link } from 'react-router';
import { Menu, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { getUserDisplayName } from '@/types/auth';

interface NavbarProps {
  onMenuClick: () => void;
}

export function Navbar({ onMenuClick }: NavbarProps) {
  const { user, logout, isProfileLoading, profileError, refreshProfile } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const displayName = getUserDisplayName(user);
  const isDoctor = user?.role === 'doctor';
  const accountLabel =
    isDoctor && isProfileLoading && !user?.firstName && !user?.lastName
      ? 'Loading profile...'
      : displayName;

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
  };

  const initials = accountLabel.slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white">
      <div className="flex h-16 items-center gap-4 px-4 sm:px-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMenuClick}
          className="lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white font-bold">
            N
          </div>
          <span className="hidden font-semibold text-foreground sm:block">
            Nushu Toolbox
          </span>
        </Link>

        <div className="flex-1" />

        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-label="Open account menu"
            className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-100"
          >
            <Avatar initials={initials} size="sm" />
            <span className="hidden text-sm font-medium text-foreground sm:block">
              {accountLabel}
            </span>
            {isDoctor && isProfileLoading ? <Spinner size="sm" /> : null}
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted transition-transform',
                isDropdownOpen && 'rotate-180'
              )}
            />
          </button>

          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-border bg-white py-1 shadow-lg">
                <div className="border-b border-border px-4 py-2">
                  <p className="text-sm font-medium text-foreground">
                    {displayName}
                  </p>
                  <p className="text-xs text-muted capitalize">{user?.role}</p>
                  {isDoctor && profileError ? (
                    <button
                      onClick={() => void refreshProfile()}
                      className="mt-2 text-xs font-medium text-primary hover:underline"
                    >
                      Retry profile load
                    </button>
                  ) : null}
                </div>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-gray-100"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
